// API method signature guidelines:
// Path parameters -> positional parameters
// Query parameters & Request body -> final `params` object parameter
export const buildApiClient = (http) => ({
  // Sensors
  fetchSensors: async () => {
    const response = await http.get('/api/sensors');
    return response.data;
  },
  fetchSensor: async (id) => {
    const response = await http.get(`/api/sensors/${id}`);
    return response.data;
  },
  createSensor: async (requestBody) => {
    const response = await http.post('/api/sensors', requestBody);
    return response.data;
  },
  updateSensor: async (id, requestBody) => {
    const response = await http.patch(`/api/sensors/${id}`, requestBody);
    return response.data;
  },
  deleteSensor: async (id) => {
    const response = await http.delete(`/api/sensors/${id}`);
    return response.data;
  },
  getSensorIdAvailability: async (id) => {
    const response = await http.get(`/api/sensors/${id}/availability`);
    return response.data;
  },
  fetchSensorEvents: async (sensorId, params, options = {}) => {
    const { signal, ...otherOptions } = options;
    const response = await http.get(`/api/sensors/${sensorId}/events`, { params, signal, ...otherOptions });
    return response.data;
  },
  // Events
  fetchEvent: async (eventId) => {
    const response = await http.get(`/api/v2/events/${eventId}`);
    return response.data;
  },
  updateEvent: async (eventId, requestBody) => {
    const response = await http.patch(`/api/v2/events/${eventId}`, requestBody);
    return response.data;
  },
  deleteEvent: async (eventId) => {
    await http.delete(`/api/v2/events/${eventId}`);
  },
  fetchAttachmentDownloadPath: async (eventId, attachment) => {
    const response = await http.get(`/api/attachments/${eventId}/${attachment}/download_path`);
    return response.data.download_path;
  },
  // S-Type
  fetchStationPlace: async (id) => {
    const response = await http.get(`/api/s_type/places/${id}`);
    return response.data;
  },
  fetchStationPlaces: async ({ isBackgroundRequest } = {}) => {
    const response = await http.get(
      '/api/s_type/places',
      // Default parameters for frontend
      // S-Type Devices don't use this parameter and thus don't see hidden
      // places (which is the very purpose of the hidden flag)
      { params: { with_hidden: '1', show_all_places: '1' }, isBackgroundRequest },
    );
    return response.data;
  },
  updateStationPlace: async (placeId, requestBody) => {
    const response = await http.patch(`/api/s_type/places/${placeId}`, requestBody);
    return response.data;
  },
  deleteStationPlace: async (placeId) => {
    await http.delete(`/api/s_type/places/${placeId}`);
  },
  fetchStationPlaceEvents: async (placeId, params) => {
    const response = await http.get(
      `/api/s_type/places/${placeId}/events`,
      { params },
    );
    return response.data;
  },
  fetchStationPlaceRecentEvents: async (placeId) => {
    const response = await http.get(`/api/s_type/places/${placeId}/recent_events`);
    return response.data;
  },
  fetchFloorPlans: async (placeId, { isBackgroundRequest } = {}) => {
    const response = await http.get(`/api/s_type/places/${placeId}/floor_plans`, { isBackgroundRequest });
    return response.data;
  },
  createFloorPlan: async (placeId, requestBody) => {
    const response = await http.post(`/api/s_type/places/${placeId}/floor_plans`, requestBody);
    return response.data;
  },
  updateFloorPlan: async (floorPlanId, requestBody) => {
    const response = await http.patch(`/api/floor_plans/${floorPlanId}`, requestBody);
    return response.data;
  },
  deleteFloorPlan: async (floorPlanId) => {
    await http.delete(`/api/floor_plans/${floorPlanId}`);
  },
  updateStationPlaceEvent: async (eventId, requestBody) => {
    const response = await http.patch(`/api/s_type/events/${eventId}`, requestBody);
    return response.data;
  },
  deleteStationPlaceEvent: async (eventId) => {
    await http.delete(`/api/s_type/events/${eventId}`);
  },
  fetchInsectDetectionResult: async (eventId, attachment) => {
    const response = await http.get(`/api/v2/events/${eventId}/insect_detections/${attachment}`);
    return response.data;
  },
  updateInsectDetectionResult: async (eventId, attachment, positions) => {
    const response = await http.put(`/api/v2/events/${eventId}/insect_detections/${attachment}`, { positions });
    return response.data;
  },
  dismissInsectDetectionUpdate: async (eventId) => {
    const response = await http.post(`/api/v2/events/${eventId}/dismiss_insect_detection_update`);
    return response.data;
  },
  mergeStationPlaceEvents: async (keeperEventId, removedEventId) => {
    const response = await http.post('/api/s_type/events/merge', {
      keeper_event_id: keeperEventId,
      removed_event_id: removedEventId,
    });
    return response.data;
  },
  importStationPlacesCsv: async (base64String) => {
    const response = await http.post('/api/s_type/places.csv', base64String, {
      headers: {
        'Content-Type': 'text/csv',
      },
    });
    return response.data;
  },
  createSTypeScannerEvent: async (requestBody) => {
    const response = await http.post('/api/s_type_scanner', requestBody);
    return response.data;
  },
  // R-Type Places
  fetchRTypePlaces: async () => {
    const response = await http.get('/api/r_type/places');
    return response.data;
  },
  fetchRTypePlace: async (placeId) => {
    const response = await http.get(`/api/r_type/places/${placeId}`);
    return response.data;
  },
  createRTypePlace: async (requestBody) => {
    const response = await http.post('/api/r_type/places', requestBody);
    return response.data;
  },
  updateRTypePlace: async (placeId, requestBody) => {
    const response = await http.patch(`/api/r_type/places/${placeId}`, requestBody);
    return response.data;
  },
  deleteRTypePlace: async (placeId) => {
    await http.delete(`/api/r_type/places/${placeId}`);
  },

  fetchRTypeFloorPlans: async (placeId, { isBackgroundRequest } = {}) => {
    const response = await http.get(`/api/r_type/places/${placeId}/floor_plans`, { isBackgroundRequest });
    return response.data;
  },

  createRTypeFloorPlan: async (placeId, requestBody) => {
    const response = await http.post(`/api/r_type/places/${placeId}/floor_plans`, requestBody);
    return response.data;
  },

  updateRTypeFloorPlan: async (floorPlanId, requestBody) => {
    const response = await http.patch(`/api/r_type/floor_plans/${floorPlanId}`, requestBody);
    return response.data;
  },

  deleteRTypeFloorPlan: async (floorPlanId) => {
    await http.delete(`/api/r_type/floor_plans/${floorPlanId}`);
  },

  // R-Type Capture Records
  fetchRTypeCaptureRecords: async (placeId, params = {}, { signal } = {}) => {
    const response = await http.get(`/api/r_type/places/${placeId}/capture_records`, { params, signal });
    return response.data;
  },
  createRTypeCaptureRecord: async (placeId, requestBody) => {
    const response = await http.post(`/api/r_type/places/${placeId}/capture_records`, requestBody);
    return response.data;
  },
  updateRTypeCaptureRecord: async (recordId, requestBody) => {
    const response = await http.patch(`/api/r_type/capture_records/${recordId}`, requestBody);
    return response.data;
  },
  deleteRTypeCaptureRecord: async (recordId) => {
    await http.delete(`/api/r_type/capture_records/${recordId}`);
  },
  // RatSense
  fetchRatSenseDetections: async (sensorId, params, options = {}) => {
    const { signal, ...otherOptions } = options;
    const response = await http.get(
      `/api/sensors/${sensorId}/rat_sense_detections`,
      { params, signal, ...otherOptions },
    );
    return response.data;
  },
  uploadToPresignedUrl: async (url, file) => {
    await http.put(url, file, {
      headers: {
        'Content-Type': file.type,
      },
    });
  },
  // Mirakun Devices
  fetchMirakunDevices: async () => {
    const response = await http.get('/api/mirakun_devices');
    return response.data;
  },
  // Documents
  fetchDocuments: async () => {
    const response = await http.get('/api/documents');
    return response.data;
  },
  // Announcements
  fetchAnnouncements: async () => {
    const response = await http.get('/api/announcements');
    return response.data.announcements;
  },
});
